import { useCallback } from 'react'
import { useDndContext, useDraggable, useDroppable } from '@dnd-kit/core'
import { X } from 'lucide-react'
import { clsx } from 'clsx'
import type { DraftPlayer } from '../types'
import styles from './PitchPlayerSlot.module.css'

// ── Types ──────────────────────────────────────────────────────────────────────

type BenchSlotProps = {
  player:         DraftPlayer | null
  dndId:          string     // "bench-gk" | "bench-0" | "bench-1" | "bench-2"
  label:          string     // "GR" | "Sub 1" | "Sub 2" | "Sub 3"
  enableDnd:      boolean    // false for bench-gk in MVP
  isWindowOpen:   boolean
  isSubbedIn?:    boolean    // historical: this bench player came on as auto-sub
  onRemove?:      (playerId: string) => void
  onClickPlayer?: (playerId: string) => void
}

const POSITION_CLASS: Record<string, string | undefined> = {
  GK:  styles.posGk,
  DEF: styles.posDef,
  MID: styles.posMid,
  FWD: styles.posFwd,
}

// ── Component ──────────────────────────────────────────────────────────────────

export function BenchSlot({
  player,
  dndId,
  label,
  enableDnd,
  isWindowOpen,
  isSubbedIn,
  onRemove,
  onClickPlayer,
}: BenchSlotProps) {
  // DnD is active only for filled slots when the window is open and DnD is enabled.
  const dndEnabled = player !== null && enableDnd && isWindowOpen

  // Empty outfield bench slots accept drops too, so a starter can be dragged straight down
  // to the bench (demote) — mirroring an empty pitch slot. The GK bench slot is excluded.
  const emptyDroppable = player === null && enableDnd && isWindowOpen && dndId !== 'bench-gk'

  // Filled slots use a STABLE, player-based id so dnd-kit's drop animation resolves to the
  // dragged player's real new node after a reorder. Empty slots keep their slot id
  // ("bench-{i}" / "bench-gk") so the drop target's position is known.
  const effId = player !== null ? `player-${player.playerId}` : dndId

  const { attributes, listeners, setNodeRef: setDragRef, isDragging } =
    useDraggable({ id: effId, disabled: !dndEnabled })

  const { setNodeRef: setDropRef, isOver } =
    useDroppable({ id: effId, disabled: !(dndEnabled || emptyDroppable) })

  const setRef = useCallback((node: HTMLDivElement | null) => {
    setDragRef(node)
    setDropRef(node)
  }, [setDragRef, setDropRef])

  // Subtle hint: show when any drag is active (user knows they can drop here)
  const { active } = useDndContext()
  const isDragActive = active !== null

  // ── Empty slot ───────────────────────────────────────────────────────────
  if (!player) {
    return (
      <div
        ref={emptyDroppable ? setDropRef : undefined}
        className={clsx(styles.slot, isOver && styles.isOver)}
        aria-label={`${label}: vazio`}
        style={{ opacity: isOver ? 1 : 0.45 }}
      >
        <div className={styles.circleEmpty} aria-hidden="true">
          <span className={styles.emptyPlus}>+</span>
        </div>
        <span className={styles.slotLabel}>{label}</span>
      </div>
    )
  }

  const posClass  = POSITION_CLASS[player.position]
  const canRemove = isWindowOpen && onRemove !== undefined

  return (
    <div
      ref={setRef}
      className={clsx(
        styles.slot,
        player.isEliminated   && styles.isEliminated,
        isDragging            && styles.isDragging,
        isOver && !isDragging && styles.isOver,
        isDragActive && !isDragging && !isOver && dndEnabled && styles.canDrag,
        dndEnabled            && styles.canDrag,
        onClickPlayer         && styles.clickable,
      )}
      aria-label={`${label}: ${player.displayName}`}
      onClick={onClickPlayer ? () => onClickPlayer(player.playerId) : undefined}
      {...(dndEnabled ? attributes : {})}
      {...(dndEnabled ? listeners  : {})}
    >
      <div className={clsx(styles.circle, posClass)}>
        {player.isEliminated && <div className={styles.elimOverlay} aria-hidden="true" />}

        {canRemove && (
          <button
            className={styles.removeBtn}
            aria-label={`Remover ${player.displayName}`}
            onPointerDown={e => e.stopPropagation()}
            onClick={e => { e.stopPropagation(); onRemove(player.playerId) }}
          >
            <X size={10} strokeWidth={3} />
          </button>
        )}

        <span className={styles.posLabel}>{player.position}</span>
      </div>

      <span className={styles.playerName} title={player.name}>
        {player.displayName}
      </span>

      {!isWindowOpen && player.gwPoints !== null && (
        <span className={styles.gwPoints}>{player.gwPoints} pts</span>
      )}

      <span className={styles.slotLabel}>{label}</span>

      {player.isEliminated && (
        <span className={styles.elimBadge} aria-label="Eliminado">⚠</span>
      )}
      {isSubbedIn && !player.isEliminated && (
        <span className={clsx(styles.subBadge, styles.subInBadge)} aria-label="Entrou">↑</span>
      )}
    </div>
  )
}
